

import React, { useEffect, useState, useCallback } from 'react';
import { useStateContext } from 'shared/contexts/ContextProvider';
import { storeService } from 'features/company/api';
import { loadList } from 'shared/api/requests';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    PieChart, Pie, Cell, ResponsiveContainer,
    ComposedChart, Line,
    Treemap, ScatterChart, Scatter, ZAxis
} from 'recharts';

const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatCurrency = (val) => `${(val / 1000).toFixed(1)}K`;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B'];

const ProductStats = () => {
    const today = formatDate(new Date());
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [selectedStore, setSelectedStore] = useState(null);
    const [activeView, setActiveView] = useState('profitability');
    const { token, showToast } = useStateContext();

    const buildQueryString = useCallback(() => {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        return params.toString();
    }, [startDate, endDate]);

    const fetchStores = useCallback(async () => {
        try {
            setLoading(true);
            const queryString = buildQueryString();
            const response = await loadList('/admin-api/stores');
            const storesData = await Promise.all(
                response.rows.map(async (store) => {
                    const jsonData = await storeService.getCategoryStats(store.id, queryString, token);
                    return { ...store, categoryStats: jsonData.response };
                })
            );
            setStores(storesData);
            if (storesData.length > 0 && !selectedStore) {
                setSelectedStore(storesData[0].id);
            }
        } catch (error) {
            showToast('Ошибка загрузки магазинов', 'error');
        } finally {
            setLoading(false);
        }
    }, [buildQueryString, token, showToast, selectedStore]);

    useEffect(() => { fetchStores(); }, []);

    const currentStore = stores.find(s => s.id === selectedStore);
    const data = currentStore?.categoryStats?.data || [];

    // 1. PROFITABILITY ANALYSIS
    const profitabilityData = data
        .map(c => ({
            name: c.product__categories__name,
            sales: c.total_sales_sum,
            profit: c.total_sales_profit,
            margin: c.total_sales_sum > 0 ? (c.total_sales_profit / c.total_sales_sum * 100) : 0
        }))
        .sort((a, b) => b.profit - a.profit);

    // 2. INVENTORY HEALTH
    const inventoryHealthData = data.map(c => ({
        name: c.product__categories__name,
        stockValue: c.closing_sum,
        salesVelocity: c.total_sales_qty,
        margin: c.total_sales_sum > 0 ? (c.total_sales_profit / c.total_sales_sum * 100) : 0
    }));

    // 3. REVENUE BREAKDOWN
    const treemapData = data
        .filter(c => c.total_sales_sum > 0)
        .map(c => ({
            name: c.product__categories__name,
            size: c.total_sales_sum,
            profit: c.total_sales_profit
        }));

    // 4. STOCK TURNOVER
    const turnoverData = data
        .map(c => ({
            name: c.product__categories__name,
            closingQty: c.closing_qty,
            salesQty: c.total_sales_qty,
            turnoverRatio: c.closing_qty > 0 ? (c.total_sales_qty / c.closing_qty * 100) : 0,
            stockValue: c.closing_sum
        }))
        .sort((a, b) => a.turnoverRatio - b.turnoverRatio);

    // 5. PROFIT MARGIN DISTRIBUTION
    const marginBuckets = { high: 0, medium: 0, low: 0, negative: 0 };
    data.forEach(c => {
        const margin = c.total_sales_sum > 0 ? (c.total_sales_profit / c.total_sales_sum * 100) : 0;
        if (margin >= 50) marginBuckets.high += c.total_sales_sum;
        else if (margin >= 30) marginBuckets.medium += c.total_sales_sum;
        else if (margin >= 0) marginBuckets.low += c.total_sales_sum;
        else marginBuckets.negative += Math.abs(c.total_sales_sum);
    });
    const marginPieData = [
        { name: 'Высокая (50%+)', value: marginBuckets.high, color: '#00C49F' },
        { name: 'Средняя (30-50%)', value: marginBuckets.medium, color: '#FFBB28' },
        { name: 'Низкая (0-30%)', value: marginBuckets.low, color: '#FF8042' },
        { name: 'Отрицательная', value: marginBuckets.negative, color: '#FF6B6B' }
    ].filter(d => d.value > 0);

    // 6. SUMMARY KPIs
    const totals = data.reduce((acc, c) => ({
        sales: acc.sales + c.total_sales_sum,
        profit: acc.profit + c.total_sales_profit,
        stock: acc.stock + c.closing_sum,
        qty: acc.qty + c.total_sales_qty
    }), { sales: 0, profit: 0, stock: 0, qty: 0 });

    const views = [
        { id: 'profitability', label: '📊 Прибыльность' },
        { id: 'inventory', label: '📦 Состояние склада' },
        { id: 'revenue', label: '💰 Структура выручки' },
        { id: 'turnover', label: '🔄 Оборачиваемость' },
        { id: 'margins', label: '📈 Распределение маржи' }
    ];

    const chartHeight = Math.max(400, data.length * 35);

    return (
        <div className="product-stats">
            {/* Фильтры */}
            <div className="filters" style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <label>
                    Магазин:
                    <select value={selectedStore || ''} onChange={e => setSelectedStore(Number(e.target.value))} style={{ marginLeft: 8 }}>
                        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </label>
                <label>Начало: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
                <label>Конец: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
                <button onClick={fetchStores} disabled={loading}>{loading ? 'Загрузка...' : 'Обновить'}</button>
            </div>

            {/* KPI карточки */}
            <div className="kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                <div style={{ padding: 16, background: '#f0f9ff', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, color: '#666' }}>Общие продажи</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{formatCurrency(totals.sales)}</div>
                </div>
                <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, color: '#666' }}>Общая прибыль</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#16a34a' }}>{formatCurrency(totals.profit)}</div>
                </div>
                <div style={{ padding: 16, background: '#fefce8', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, color: '#666' }}>Средняя маржа</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totals.sales > 0 ? (totals.profit / totals.sales * 100).toFixed(1) : 0}%</div>
                </div>
                <div style={{ padding: 16, background: '#faf5ff', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, color: '#666' }}>Стоимость остатков</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{formatCurrency(totals.stock)}</div>
                </div>
            </div>

            {/* Вкладки */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {views.map(v => (
                    <button
                        key={v.id}
                        onClick={() => setActiveView(v.id)}
                        style={{
                            padding: '8px 16px', borderRadius: 4, border: 'none', cursor: 'pointer',
                            background: activeView === v.id ? '#3b82f6' : '#e5e7eb',
                            color: activeView === v.id ? '#fff' : '#374151'
                        }}
                    >{v.label}</button>
                ))}
            </div>

            {/* Графики */}
            <div style={{ background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {activeView === 'profitability' && (
                    <>
                        <h3>Все категории по прибыли (Продажи vs Прибыль)</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>Сравнение выручки и фактической прибыли для выявления высокомаржинальных категорий</p>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <ComposedChart data={profitabilityData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={formatCurrency} xAxisId="bottom" />
                                <XAxis type="number" orientation="top" xAxisId="top" unit="%" domain={[0, 100]} />
                                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(val, name) => name === 'Маржа %' ? `${val.toFixed(1)}%` : formatCurrency(val)} />
                                <Legend />
                                <Bar dataKey="sales" fill="#8884d8" name="Продажи" xAxisId="bottom" />
                                <Bar dataKey="profit" fill="#82ca9d" name="Прибыль" xAxisId="bottom" />
                                <Line dataKey="margin" stroke="#ff7300" name="Маржа %" xAxisId="top" dot={{ fill: '#ff7300', r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </>
                )}

                {activeView === 'inventory' && (
                    <>
                        <h3>Матрица состояния склада</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>X: Скорость продаж | Y: Стоимость остатков | Размер: Маржа — Найдите медленно движущийся дорогой товар</p>
                        <ResponsiveContainer width="100%" height={500}>
                            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                <CartesianGrid />
                                <XAxis dataKey="salesVelocity" name="Кол-во продаж" label={{ value: 'Кол-во продаж', position: 'bottom' }} />
                                <YAxis dataKey="stockValue" name="Стоимость остатков" tickFormatter={formatCurrency} label={{ value: 'Стоимость остатков', angle: -90, position: 'insideLeft' }} />
                                <ZAxis dataKey="margin" range={[50, 400]} name="Маржа %" />
                                <Tooltip 
                                    cursor={{ strokeDasharray: '3 3' }} 
                                    formatter={(val, name) => name === 'Стоимость остатков' ? formatCurrency(val) : val}
                                    labelFormatter={(_, payload) => payload[0]?.payload?.name || ''}
                                />
                                <Scatter data={inventoryHealthData} fill="#8884d8">
                                    {inventoryHealthData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Scatter>
                            </ScatterChart>
                        </ResponsiveContainer>
                    </>
                )}

                {activeView === 'revenue' && (
                    <>
                        <h3>Структура выручки по категориям</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>Пропорциональный вид вклада в выручку — большие блоки = выше выручка</p>
                        <ResponsiveContainer width="100%" height={500}>
                            <Treemap data={treemapData} dataKey="size" nameKey="name" stroke="#fff" fill="#8884d8">
                                {treemapData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                <Tooltip formatter={(val) => formatCurrency(val)} />
                            </Treemap>
                        </ResponsiveContainer>
                    </>
                )}

                {activeView === 'turnover' && (
                    <>
                        <h3>Оборачиваемость всех категорий</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>Категории с низкими продажами относительно остатков — риск залежавшегося товара</p>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <ComposedChart data={turnoverData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" xAxisId="bottom" />
                                <XAxis type="number" orientation="top" xAxisId="top" unit="%" />
                                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="closingQty" fill="#ff8042" name="Кол-во на складе" xAxisId="bottom" />
                                <Bar dataKey="salesQty" fill="#00C49F" name="Кол-во продаж" xAxisId="bottom" />
                                <Line dataKey="turnoverRatio" stroke="#8884d8" name="Оборачиваемость %" xAxisId="top" dot={{ fill: '#8884d8', r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </>
                )}

                {activeView === 'margins' && (
                    <>
                        <h3>Распределение продаж по уровню маржи</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>Какая доля выручки приходится на высоко- и низкомаржинальные товары?</p>
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={marginPieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={150}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                    {marginPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip formatter={(val) => formatCurrency(val)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </>
                )}
            </div>

            {/* Информация о данных */}
            <div style={{ marginTop: 16, color: '#666', fontSize: 14 }}>
                Всего категорий: {data.length} | Период: {startDate} — {endDate}
            </div>
        </div>
    );
};

export default ProductStats;